import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import moment from 'moment/moment';

import Button from '@material-ui/core/Button';
import ButtonGroup from '@material-ui/core/ButtonGroup';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import CircularProgress from '@material-ui/core/CircularProgress';
import GetAppIcon from '@material-ui/icons/GetApp';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';

import Api from '../../../libraries/api';

import '@fortawesome/fontawesome-free/css/all.css';
import './index.css';

const SURVEYOR_USERS_URL =
    '/users?limit=1000&page=1&orderBy=name&sortedBy=asc&is_active=1&role=Surveyor';

const formatUiDate = (v) => (v ? moment(v).format('DD MMM YYYY') : '—');

const surveyorShortName = (name) => {
    const trimmed = (name || '').trim();
    if (!trimmed) return '—';
    return trimmed.split(/\s+/)[0];
};

const buildWeekDays = (weekStart) => {
    const monday = moment(weekStart).startOf('isoWeek');
    return Array.from({ length: 7 }, (_, i) => monday.clone().add(i, 'days'));
};

const entryStartDay = (item) =>
    item.survey_start_date ||
    (item.start ? moment(item.start).format('YYYY-MM-DD') : null);

const entryEndDay = (item) => {
    if (item.survey_done_date) {
        return moment(item.survey_done_date).format('YYYY-MM-DD');
    }
    if (item.end) {
        return moment(item.end).format('YYYY-MM-DD');
    }
    return entryStartDay(item);
};

const isDayInEntryRange = (day, item) => {
    const start = entryStartDay(item);
    const end = entryEndDay(item);
    if (!start) return false;
    const d = day.format('YYYY-MM-DD');
    const e = end || start;
    return d >= start && d <= e;
};

const cellKey = (surveyorId, day) => `${surveyorId}:${day.format('YYYY-MM-DD')}`;

const SurveyMonitorList = () => {
    const [loading, setLoading] = useState(false);
    const [surveyors, setSurveyors] = useState([]);
    const [entries, setEntries] = useState([]);
    const [weekStart, setWeekStart] = useState(() => moment().startOf('isoWeek'));
    const [detailOpen, setDetailOpen] = useState(false);
    const [selected, setSelected] = useState(null);

    const weekDays = useMemo(() => buildWeekDays(weekStart), [weekStart]);

    const rangeStart = weekDays[0].format('YYYY-MM-DD');
    const rangeEnd = weekDays[6].format('YYYY-MM-DD');

    const weekTitle = `${weekDays[0].format('dddd, D MMM YYYY')} – ${weekDays[6].format('dddd, D MMM YYYY')}`;

    const fetchSurveyors = useCallback(() => {
        return Api.get(SURVEYOR_USERS_URL).then((resp) => {
            const list = Array.isArray(resp?.data) ? resp.data : [];
            return list
                .filter((u) => u.is_active === 1 || u.is_active === true)
                .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        });
    }, []);

    const fetchEntries = useCallback((start, end) => {
        const url = `/surveyor-monitoring?start=${start}&end=${end}&limit=0`;
        return Api.get(url).then((resp) => {
            const raw = resp?.data;
            return Array.isArray(raw) ? raw : [];
        });
    }, []);

    const loadWeekData = useCallback(() => {
        setLoading(true);
        Promise.all([fetchSurveyors(), fetchEntries(rangeStart, rangeEnd)])
            .then(([userList, entryList]) => {
                setSurveyors(userList);
                setEntries(entryList);
            })
            .catch(() => {
                toast.error('Failed to load surveyor schedule.', { position: toast.POSITION.BOTTOM_RIGHT });
                setSurveyors([]);
                setEntries([]);
            })
            .finally(() => setLoading(false));
    }, [fetchSurveyors, fetchEntries, rangeStart, rangeEnd]);

    useEffect(() => {
        loadWeekData();
    }, [loadWeekData]);

    const surveyorRows = useMemo(() => {
        const byId = {};
        surveyors.forEach((s) => {
            byId[s.id] = s;
        });
        entries.forEach((item) => {
            if (item.surveyor_id && !byId[item.surveyor_id]) {
                byId[item.surveyor_id] = {
                    id: item.surveyor_id,
                    name: item.surveyor_name || 'Unknown',
                };
            }
        });
        return Object.values(byId).sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    }, [surveyors, entries]);

    const cellMap = useMemo(() => {
        const map = {};
        entries.forEach((item) => {
            const surveyorId = item.surveyor_id;
            if (!surveyorId) return;

            weekDays.forEach((day) => {
                if (!isDayInEntryRange(day, item)) return;
                const key = cellKey(surveyorId, day);
                if (!map[key]) map[key] = [];
                map[key].push(item);
            });
        });
        return map;
    }, [entries, weekDays]);

    const handlePrevWeek = () => {
        setWeekStart((prev) => moment(prev).subtract(1, 'week').startOf('isoWeek'));
    };

    const handleNextWeek = () => {
        setWeekStart((prev) => moment(prev).add(1, 'week').startOf('isoWeek'));
    };

    const handleThisWeek = () => {
        setWeekStart(moment().startOf('isoWeek'));
    };

    const handleExport = () => {
        setLoading(true);
        const route = `/export/surveyor-monitoring?start=${rangeStart}&end=${rangeEnd}`;
        Api.get(route)
            .then((resp) => {
                const url = resp?.data?.url;
                if (url) {
                    window.open(url);
                } else {
                    toast.error('Export failed — URL not found.', { position: toast.POSITION.BOTTOM_RIGHT });
                }
            })
            .catch(() => {
                toast.error('Export failed, please try again or check your permissions.', {
                    position: toast.POSITION.BOTTOM_RIGHT,
                });
            })
            .finally(() => setLoading(false));
    };

    const openDetail = (item) => {
        setSelected({
            project_id: item.project_id,
            case_number: item.case_number,
            vessel_name: item.vessel_name,
            surveyor_name: item.surveyor_name,
            survey_start_date: item.survey_start_date,
            survey_done_date: item.survey_done_date,
            location_survey: item.location_survey || '',
            title: item.title,
        });
        setDetailOpen(true);
    };

    const renderScheduleBlock = (item) => {
        const caseNumber = item.case_number || '';
        const vessel = item.vessel_name || '';
        const location = item.location_survey || '—';
        const detailPath = item.project_id ? `/projects/detail/${item.project_id}` : null;

        const tooltip = [
            caseNumber && `Case: ${caseNumber}`,
            vessel && `Vessel: ${vessel}`,
            item.location_survey && `Location: ${item.location_survey}`,
            item.survey_start_date && `Start: ${formatUiDate(item.survey_start_date)}`,
            item.survey_done_date && `Done: ${formatUiDate(item.survey_done_date)}`,
        ]
            .filter(Boolean)
            .join('\n');

        const renderTitleLine = () => {
            if (caseNumber && vessel) {
                return (
                    <>
                        {detailPath ? (
                            <Link
                                to={detailPath}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="survey-schedule-block__case-link"
                                onClick={(e) => e.stopPropagation()}
                            >
                                {caseNumber}
                            </Link>
                        ) : (
                            <span className="survey-schedule-block__case">{caseNumber}</span>
                        )}
                        {' - '}
                        {vessel}
                    </>
                );
            }
            if (caseNumber) {
                return detailPath ? (
                    <Link
                        to={detailPath}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="survey-schedule-block__case-link"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {caseNumber}
                    </Link>
                ) : (
                    <span className="survey-schedule-block__case">{caseNumber}</span>
                );
            }
            return vessel || item.title || '—';
        };

        return (
            <button
                key={item.id}
                type="button"
                className="survey-schedule-block survey-schedule-block--survey"
                title={tooltip}
                onClick={() => openDetail(item)}
            >
                <span className="survey-schedule-block__vessel">{renderTitleLine()}</span>
                <span className="survey-schedule-block__location">{location}</span>
            </button>
        );
    };

    const renderDetailDialog = () => {
        const p = selected || {};
        const detailLink = p.project_id && `/projects/detail/${p.project_id}`;

        return (
            <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Survey Monitoring Detail</DialogTitle>
                <DialogContent>
                    <dl className="survey-monitor-detail-grid">
                        <dt>Case Number</dt>
                        <dd>
                            {detailLink ? (
                                <Link to={detailLink} target="_blank" rel="noopener noreferrer">
                                    {p.case_number || '—'}
                                </Link>
                            ) : (
                                p.case_number || '—'
                            )}
                        </dd>
                        <dt>Vessel Name</dt>
                        <dd>{p.vessel_name || '—'}</dd>
                        <dt>Surveyor Name</dt>
                        <dd>{p.surveyor_name || '—'}</dd>
                        <dt>Survey Start Date</dt>
                        <dd>{formatUiDate(p.survey_start_date)}</dd>
                        <dt>Survey Done Date</dt>
                        <dd>{formatUiDate(p.survey_done_date)}</dd>
                        <dt>Survey Location</dt>
                        <dd>{p.location_survey || '—'}</dd>
                    </dl>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDetailOpen(false)} color="primary" variant="contained">
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        );
    };

    const todayStr = moment().format('YYYY-MM-DD');

    return (
        <>
            <div className="row main-content">
                <div className="col-12 px-lg-5">
                    <h2 className="page-title">Survey Monitoring List</h2>
                    <nav aria-label="breadcrumb">
                        <ol className="breadcrumb">
                            <li className="breadcrumb-item">
                                <Link to="/">Home</Link>
                            </li>
                            <li className="breadcrumb-item active" aria-current="page">
                                Survey Monitoring List
                            </li>
                        </ol>
                    </nav>

                    <div className="table-wrapper">
                        <div className="row mb-3">
                            <div className="col-sm-12 col-md-6 d-flex flex-wrap align-items-center survey-schedule-toolbar-row">
                                <ButtonGroup
                                    variant="contained"
                                    color="primary"
                                    className="survey-schedule-nav"
                                    disabled={loading}
                                >
                                    <Button aria-label="Previous week" onClick={handlePrevWeek}>
                                        <ChevronLeftIcon />
                                    </Button>
                                    <Button onClick={handleThisWeek} className="survey-schedule-nav-today">
                                        This week
                                    </Button>
                                    <Button aria-label="Next week" onClick={handleNextWeek}>
                                        <ChevronRightIcon />
                                    </Button>
                                </ButtonGroup>
                                <span className="survey-schedule-toolbar-title">{weekTitle}</span>
                            </div>
                            <div className="col-sm-12 col-md-6 text-right mb-3 d-flex justify-content-md-end align-items-end">
                                <Button
                                    variant="contained"
                                    color="secondary"
                                    className="mb-3 mb-md-0"
                                    onClick={handleExport}
                                    disabled={loading}
                                >
                                    {loading ? <CircularProgress size={18} color="inherit" /> : <GetAppIcon />}
                                    &nbsp; Export
                                </Button>
                            </div>
                        </div>
                        <div className="survey-schedule-wrapper position-relative">
                            {loading && (
                                <div className="survey-schedule-loading">
                                    <CircularProgress />
                                </div>
                            )}
                            <table className="survey-schedule-table">
                                <thead>
                                    <tr>
                                        <th className="survey-schedule-surveyor-col">Surveyor</th>
                                        {weekDays.map((day) => (
                                            <th
                                                key={day.format('YYYY-MM-DD')}
                                                className={
                                                    day.format('YYYY-MM-DD') === todayStr
                                                        ? 'survey-schedule-day-col is-today'
                                                        : 'survey-schedule-day-col'
                                                }
                                            >
                                                {day.format('dddd, D MMM YYYY')}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {surveyorRows.length === 0 && !loading ? (
                                        <tr>
                                            <td colSpan={8} className="text-center text-muted py-4">
                                                No active surveyors found.
                                            </td>
                                        </tr>
                                    ) : (
                                        surveyorRows.map((surveyor) => (
                                            <tr key={surveyor.id}>
                                                <th
                                                    className="survey-schedule-surveyor-col"
                                                    title={surveyor.name}
                                                    scope="row"
                                                >
                                                    {surveyorShortName(surveyor.name)}
                                                </th>
                                                {weekDays.map((day) => {
                                                    const key = cellKey(surveyor.id, day);
                                                    const cellEntries = cellMap[key] || [];
                                                    const isToday = day.format('YYYY-MM-DD') === todayStr;

                                                    return (
                                                        <td
                                                            key={key}
                                                            className={
                                                                isToday
                                                                    ? 'survey-schedule-cell is-today'
                                                                    : 'survey-schedule-cell'
                                                            }
                                                        >
                                                            {cellEntries.map((item) => renderScheduleBlock(item))}
                                                        </td>
                                                    );
                                                })}
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
            {renderDetailDialog()}
            <ToastContainer autoClose={3000} />
        </>
    );
};

export default SurveyMonitorList;
